import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import SitePricingPlanFormModal from 'src/features/pricing/sites/components/form/SitePricingPlanFormModal';
import { PricingPlan } from 'src/features/pricing/types';
import { pricingPlanMessages } from 'src/features/pricing/messages';
import { useCreateSitePricingPlan } from 'src/features/pricing/sites/api/useCreateSitePricingPlan';
import { LIST_SITE_PRICING_PLANS_RESOURCE } from 'src/features/pricing/sites/api/useListSitePricingPlans';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';

interface PricingPlanFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPricingPlan?: PricingPlan;
    children?: React.ReactNode;
}

export default function CreateSitePricingPlanFormModal<TData>({
    className,
    semantics,
}: PricingPlanFormModalProps<TData>) {
    const { formatMessage, locale } = useIntl();
    const { siteId = '' } = useParams();
    const { parkingId } = useParking();

    const { mutate, isPending } = useCreateSitePricingPlan({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SITE_PRICING_PLANS_RESOURCE] });
            toast({
                title: formatMessage(pricingPlanMessages.createPricingPlanVersionSuccess),
                description: formatMessage(pricingPlanMessages.createPricingPlanVersionSuccessDescription, {
                    entry: success.translations.find((translation) => translation.language === locale)?.name,
                }),
            });
        },
    });

    return (
        <SitePricingPlanFormModal
            className={className}
            mutate={(data, onSuccess, onError) => mutate({ ...data, site: siteId, parkingId }, { onSuccess, onError })}
            title={formatMessage(generalMessages.createEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.ADD}
            isPending={isPending}
            subtitle={formatMessage(pricingPlanMessages.addPricingPlanSubtitle)}
        />
    );
}
